import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTheme } from '../../../contexts/ThemeContext';
import { DocumentIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { t } from 'i18next';

interface FileUploadProps {
  onChange: (files: File[]) => void;
  value?: File[];
  disabled?: boolean;
}

export default function FileUpload({ onChange, value = [], disabled = false }: FileUploadProps) {
  const { theme } = useTheme();
  const [error, setError] = useState<string | null>(null);
  
  // Maximum number of files allowed (from server config)
  const MAX_FILES = 5;

  const onDrop = useCallback((acceptedFiles: File[]) => {
    // Check if adding these files would exceed the limit
    if (value.length + acceptedFiles.length > MAX_FILES) {
      setError(`You can only upload a maximum of ${MAX_FILES} files`);
      return;
    }
    
    // Clear any previous errors
    setError(null);
    onChange([...value, ...acceptedFiles]);
  }, [onChange, value, MAX_FILES]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled
  });

  const removeFile = (index: number) => {
    const newFiles = [...value];
    newFiles.splice(index, 1);
    onChange(newFiles);
  };

  return (
    <div>
      {/* Error message */}
      {error && (
        <div className={`mb-3 flex items-center text-sm ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>
          <ExclamationCircleIcon className="h-5 w-5 mr-1" />
          <span>{error}</span>
        </div>
      )}
      <div
        {...getRootProps()}
        className={`
          mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md
          ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
          ${isDragActive ? 'border-blue-400' : ''}
          ${error ? (theme === 'dark' ? 'border-red-500' : 'border-red-400') : ''}
          ${theme === 'dark'
            ? 'border-gray-600/50 bg-gray-900/30 hover:border-gray-500/70 hover:bg-gray-900/50'
            : 'border-gray-300/60 bg-gray-50/50 hover:border-gray-400/70 hover:bg-gray-100/50'
          }
        `}
      >
        <div className="space-y-1 text-center">
          <DocumentIcon className={`mx-auto h-12 w-12 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
          }`} />
          <div className=" text-sm">
            <input {...getInputProps()} />
            <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>
              <span className="text-blue-500">{t('tickets.uploadFile')}</span>
              {t('tickets.dragndrop')}
            </p>
            <p className={`text-xs mt-1 ${
              theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
            }`}>
              {t('tickets.maxFileSize')}
            </p>
          </div>
        </div>
      </div>

      {/* File List */}
      {value.length > 0 && (
        <ul className={`mt-4 divide-y ${
          theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'
        }`}>
          {value.map((file, index) => (
            <li key={index} className="py-3 flex justify-between items-center">
              <div className={`flex items-center ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                <DocumentIcon className="h-5 w-5 mr-2" />
                <span className="text-sm">{file.name}</span>
              </div>
              <button
                type="button"
                onClick={() => removeFile(index)}
                className={`text-sm font-medium ${
                  theme === 'dark'
                    ? 'text-red-400 hover:text-red-300'
                    : 'text-red-600 hover:text-red-500'
                }`}
              >
                {t('tickets.removeAttachment')}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
